<div class="cp-root grid min-h-[100dvh] place-items-center px-5 py-16">
  <a
    [attr.href]="exitUrl"
    (click)="exit($event)"
    aria-label="Close"
    class="fixed left-4 top-4 grid h-9 w-9 place-items-center rounded-full text-cp-muted transition-colors hover:bg-cp-fill hover:text-cp-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-cp-accent sm:left-6 sm:top-6">
    <ng-icon name="phosphorX" size="18" aria-hidden="true" />
  </a>

  <main class="w-full max-w-[340px]">
    <div class="flex flex-col items-center text-center">
      <a routerLink="/" aria-label="creditpath.io home" class="rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cp-accent">
        <img src="assets/img/creditpathlogo.webp" alt="" width="36" height="36" class="h-9 w-9" />
      </a>
      <h1 class="mt-6 text-[20px] font-[510] tracking-[-0.011em] text-cp-ink">
        {{ isSignIn ? 'Log in to creditpath.io' : 'Create your creditpath.io account' }}
      </h1>
    </div>

    @if (notice && !bannerError) {
      <p role="status" class="mt-8 flex items-start gap-2.5 rounded-lg bg-emerald-500/10 px-3.5 py-3 text-[14px] leading-snug text-cp-ink">
        <ng-icon name="phosphorCheck" size="16" class="mt-0.5 shrink-0 text-emerald-500" aria-hidden="true" />
        {{ notice }}
      </p>
    }

    @if (bannerError) {
      <p role="alert" class="mt-8 flex items-start gap-2.5 rounded-lg bg-cp-danger/10 px-3.5 py-3 text-[14px] leading-snug text-cp-danger">
        <ng-icon name="phosphorWarningCircle" size="16" class="mt-0.5 shrink-0" aria-hidden="true" />
        {{ bannerError }}
      </p>
    }

    <!-- Google first when enabled, as on Linear. Hidden by environment for work-email-only sign-in. -->
    @if (enableGoogleSignIn) {
      <button type="button" (click)="handleGoogleSignIn()" [disabled]="isLoading" [class]="btnSecondary + ' mt-8 w-full gap-2.5'">
        <svg class="h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path>
          <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path>
          <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path>
          <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path>
        </svg>
        Continue with Google
      </button>
      <div class="my-6 flex items-center gap-3 text-[12px] text-cp-subtle">
        <span class="h-px flex-1 bg-cp-line"></span>or<span class="h-px flex-1 bg-cp-line"></span>
      </div>
    }

    <!-- Field ids and names unchanged from v2 (email, password, rememberMe). -->
    <form (ngSubmit)="onSubmit()" [class]="enableGoogleSignIn ? 'space-y-4' : 'mt-8 space-y-4'">
      <div class="grid gap-2">
        <label for="email" class="text-[13px] text-cp-muted">Email address</label>
        <input
          id="email"
          name="email"
          type="email"
          autocomplete="username"
          required
          [(ngModel)]="email"
          [attr.aria-invalid]="!!emailError"
          [attr.aria-describedby]="emailError ? 'email-error' : null"
          [class]="field + (emailError ? ' !border-cp-danger' : '')"
          placeholder="you@yourfirm.com" />
        @if (emailError) {
          <p id="email-error" class="text-[13px] text-cp-danger">{{ emailError }}</p>
        }
      </div>

      <div class="grid gap-2">
        <div class="flex items-baseline justify-between">
          <label for="password" class="text-[13px] text-cp-muted">Password</label>
          @if (isSignIn) {
            <a routerLink="/forgot-password" class="rounded-sm text-[13px] text-cp-muted transition-colors hover:text-cp-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-cp-accent">Forgot password?</a>
          }
        </div>
        <input
          id="password"
          name="password"
          type="password"
          [attr.autocomplete]="isSignIn ? 'current-password' : 'new-password'"
          required
          [(ngModel)]="password"
          (input)="onPasswordChange()"
          [attr.aria-invalid]="!!passwordError"
          [attr.aria-describedby]="isSignIn ? (passwordError ? 'password-error' : null) : 'password-requirements'"
          [class]="field + (passwordError ? ' !border-cp-danger' : '')" />
        @if (passwordError) {
          <p id="password-error" class="text-[13px] text-cp-danger">{{ passwordError }}</p>
        }

        @if (showPasswordRequirements) {
          <ul id="password-requirements" aria-live="polite" class="mt-1 space-y-1.5">
            @for (req of requirements; track req.key) {
              <li [class]="'flex items-center gap-2 text-[13px] transition-colors ' + (passwordValidation[req.key] ? 'text-cp-accent-fg' : 'text-cp-subtle')">
                <ng-icon [name]="passwordValidation[req.key] ? 'phosphorCheck' : 'phosphorCircle'" size="13" aria-hidden="true" />
                {{ req.label }}
                <span class="sr-only">{{ passwordValidation[req.key] ? '(met)' : '(not met)' }}</span>
              </li>
            }
          </ul>
        }
      </div>

      @if (isSignIn) {
        <label class="flex w-fit cursor-pointer items-center gap-2 text-[13px] text-cp-muted">
          <input type="checkbox" name="rememberMe" [(ngModel)]="rememberMe" class="h-4 w-4 rounded border-cp-line-strong accent-[rgb(var(--cp-accent))]" />
          Remember me
        </label>
      }

      <button type="submit" [disabled]="isLoading || (!isSignIn && !isPasswordValid)" [class]="btnPrimary + ' !mt-6 w-full'">
        @if (isLoading) {
          <ng-icon name="phosphorCircleNotch" size="16" class="animate-spin motion-reduce:animate-none" aria-hidden="true" />
        }
        {{ isSignIn ? 'Log in' : 'Create account' }}
      </button>

      @if (!isSignIn) {
        <p class="text-center text-[12px] leading-relaxed text-cp-subtle">
          By signing up, you agree to our
          <a routerLink="/terms-and-conditions" target="_blank" rel="noopener" class="text-cp-muted underline underline-offset-2 hover:text-cp-ink">Terms and Conditions<span class="sr-only"> (opens in a new tab)</span></a>
          and
          <a routerLink="/privacy-policy" target="_blank" rel="noopener" class="text-cp-muted underline underline-offset-2 hover:text-cp-ink">Privacy Policy<span class="sr-only"> (opens in a new tab)</span></a>.
        </p>
      }
    </form>

    <p class="mt-8 text-center text-[13px] text-cp-muted">
      {{ isSignIn ? "Don't have an account?" : 'Already have an account?' }}
      <button type="button" (click)="toggleView()" class="ml-0.5 rounded-sm text-cp-ink underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-cp-accent">
        {{ isSignIn ? 'Sign up' : 'Log in' }}
      </button>
    </p>
  </main>
</div>
